import { SwipeCard } from './SwipeCard';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../ui/Icon';
import styles from './MailSurface.module.css';

export function GmailMark() {
  return <svg viewBox="0 0 48 36" width="20" height="15" role="img" aria-label="Gmail">
    <path fill="#4285F4" d="M3 36h7V15L0 7.5V33a3 3 0 0 0 3 3Z" />
    <path fill="#34A853" d="M38 36h7a3 3 0 0 0 3-3V7.5L38 15Z" />
    <path fill="#EA4335" d="M10 15V2.5L24 13 38 2.5V15L24 25.5Z" />
    <path fill="#C5221F" d="M0 7.5V5a5 5 0 0 1 8-4l2 1.5V15Z" />
    <path fill="#FBBC04" d="m38 2.5 2-1.5a5 5 0 0 1 8 4v2.5L38 15Z" />
  </svg>;
}

export function MailGlyph({ name, size = 15 }: { name: 'external' | 'reply' | 'clock'; size?: number }) {
  const paths = {
    external: 'M14 3h7v7M21 3 10 14M10 3H3v18h18v-7',
    reply: 'm9 4-6 6 6 6M3 10h10a7 7 0 0 1 7 7v3',
    clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7v5l3 2',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name]} /></svg>;
}

export function MailHint({ children, text }: { children: ReactNode; text: string }) {
  const id = useId();
  return <span className={styles.hint} tabIndex={0} aria-describedby={id}>
    {children}<span id={id} role="tooltip" className={styles.tooltip}>{text}</span>
  </span>;
}

export function MailSurface({ children, label, account, gmailUrl, demo = false }: {
  children: ReactNode; label: string; account?: string | undefined; gmailUrl?: string; demo?: boolean;
}) {
  return <section className={styles.surface} aria-label={label}>
    <div className={styles.chrome}>
      <span className={styles.identity}><GmailMark />
        {demo ? <MailHint text="This is an example. Your connected conversation will open in Gmail.">
          <a href="#" onClick={e => e.preventDefault()}>Open in Gmail <MailGlyph name="external" size={13} /></a>
        </MailHint> : gmailUrl ? <a href={gmailUrl} target="_blank" rel="noopener noreferrer">Open in Gmail <MailGlyph name="external" size={13} /></a> : <strong>{label}</strong>}
      </span>
      {account && <span className={styles.account} title={account}>{account}</span>}
      {demo && <span className={styles.example}>Example · not sent</span>}
    </div>
    <div className={styles.content}>{children}</div>
  </section>;
}

export function MailSubject({ subject, inbox = false }: { subject: string; inbox?: boolean }) {
  return <div className={styles.subject}><h3>{subject}</h3>{inbox && <span>Inbox</span>}</div>;
}

export function MailMessage({ from, to, subject, body, timestamp, children }: {
  from: string; to?: string; subject?: string | undefined; body?: string | undefined; timestamp?: string; children?: ReactNode;
}) {
  const sender = from.match(/^(.*?)\s*<([^>]+)>$/);
  return <div className={styles.message}>
    <span className={styles.avatar} aria-hidden="true">{(sender?.[1] || from).charAt(0).toUpperCase()}</span>
    <div className={styles.messageContent}>
      <div className={styles.senderLine}>
        <span><strong>{sender?.[1] || from}</strong>{sender && <span className={styles.address}> &lt;{sender[2]}&gt;</span>}</span>
        {timestamp && <span className={styles.timestamp}>{timestamp}</span>}
      </div>
      <details className={styles.envelope}>
        <summary>to {to || 'me'} <Icon name="chevron-down" size={12} /></summary>
        <dl><dt>from:</dt><dd>{from}</dd><dt>to:</dt><dd>{to || 'me'}</dd>{subject && <><dt>subject:</dt><dd>{subject}</dd></>}</dl>
      </details>
      {body !== undefined && <div className={styles.body}>{body || '\u00a0'}</div>}
      {children}
    </div>
  </div>;
}

export function MailComposer({ to, body, onChange, edited = false, ai = true, disabled = false, writing = false, showEditHint = true, children, fields, actions }: {
  to: string; body: string; onChange: (value: string) => void; edited?: boolean; ai?: boolean;
  disabled?: boolean; writing?: boolean; showEditHint?: boolean; children?: ReactNode; fields?: ReactNode; actions?: ReactNode;
}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!textarea.current) return;
    textarea.current.style.height = 'auto';
    textarea.current.style.height = `${textarea.current.scrollHeight}px`;
  }, [body]);
  return <section className={styles.composer} aria-label="Reply composer">
    <div className={styles.composerHeader}>
      <span className={styles.replyTo}><MailGlyph name="reply" size={17} /><span>{to}</span></span>
      <span className={styles.aiBadge} data-ai={ai}>
        {ai && <Icon name="sparkles" size={13} />}
        {edited ? (ai ? 'AI draft · edited by you' : 'Edited by you') : ai ? (writing ? 'AI is writing…' : 'AI draft') : 'Template draft'}
      </span>
    </div>
    {fields}
    <div className={styles.writingArea}>
      <textarea ref={textarea} className={styles.replyBody} aria-label="Message" value={body}
        onChange={e => onChange(e.target.value)} readOnly={disabled} data-ai={ai && !edited}
        rows={5} spellCheck placeholder="Write your reply…" />
      {showEditHint && <span className={styles.editHint}>{disabled ? 'Reply locked' : writing ? 'Click here to take over' : 'Click anywhere to edit'}</span>}
    </div>
    {children}
    {actions && <div className={styles.composerActions}>{actions}</div>}
  </section>;
}

const examples = [
  { subject: 'Your next reply, ready to review', from: 'Alex Morgan <alex@example.com>', to: 'Alex Morgan (alex@example.com)',
    incoming: 'How does replying from Home work?',
    reply: 'Hi Alex,\n\nEcho prepares a reply using the conversation and my saved context. I can change every word before I send it.\n\nNothing sends until I approve it. This example lets me try the experience.' },
  { subject: 'A useful next step', from: 'Jordan Lee <jordan@example.com>', to: 'Jordan Lee (jordan@example.com)',
    incoming: 'How do you decide who to reach out to next?',
    reply: 'Hi Jordan,\n\nAuto Progress works on my current mission: researching people, finding useful next steps, and preparing outreach for me to review.\n\nI stay in control of what actually gets sent.' },
  { subject: 'Make it sound like you', from: 'Sam Rivera <sam@example.com>', to: 'Sam Rivera (sam@example.com)',
    incoming: 'Can I ask Echo to change a draft?',
    reply: 'Hi Sam,\n\nYes. I can edit the draft here, or ask in the chat below. Echo knows which real card I’m looking at and what I’m trying to accomplish.\n\nThis is just an example. No email will be sent.' },
];
export function EmailDemo() {
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState(0);
  const [finished, setFinished] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const next = () => { setDirection('right'); if (index < examples.length - 1) setIndex(index + 1); else setFinished(true); };
  const previous = () => { setDirection('left'); setIndex(Math.max(0, index - 1)); };
  return <section className={styles.walkthrough} aria-label="Automatic reply example">
    <div className={styles.exampleProgress}>
      <span className={styles.pagination} aria-label={finished ? 'Examples complete' : `Example ${index + 1} of ${examples.length}`}>
        {examples.map((_, n) => <i key={n} data-active={!finished && n === index} data-complete={finished || n < index} />)}
        <span>{finished ? 'Complete' : `${index + 1} / ${examples.length}`}</span>
      </span>
    </div>
    {finished ? <div className={styles.demoComplete}><Icon name="sparkles" size={28} /><strong>Make your next move.</strong>
      <p>Review a reply, work on your mission, or start a conversation with Echo.</p>
      <button type="button" onClick={() => { setIndex(0); setRound(round + 1); setDirection('right'); setFinished(false); }}>Replay examples</button>
    </div> : <ExampleEmail example={examples[index]!} itemKey={`${round}:${index}`} direction={direction}
      previews={examples.slice(index + 1).map(example => ({ title: example.subject, caption: 'Example email' }))}
      onNext={next} onPrevious={index > 0 ? previous : undefined} />}
  </section>;
}
function ExampleEmail({ example, itemKey, previews, direction, onNext, onPrevious }: {
  example: typeof examples[number]; itemKey: string; previews: { title: string; caption: string }[];
  direction: 'left' | 'right'; onNext: () => void; onPrevious: (() => void) | undefined;
}) {
  return <SwipeCard itemKey={itemKey} entryDirection={direction} previews={previews}
    onRight={onNext} onLeft={onPrevious} exitLeft rightLabel="Next (Send)" leftLabel="Previous (Dismiss)"
    rightConfirmation={<><span className={styles.confirmCheck}><svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="22" /><path d="m14 24 7 7 14-15" /></svg></span>
      <strong className={styles.confirmTitle}>Example sent</strong><span className={styles.confirmCaption}>Just a preview. No email was sent.</span></>}>
    {({ right, left, busy }) => <ExampleMessage key={itemKey} example={example} onNext={right} onPrevious={onPrevious ? left : undefined} busy={busy} />}
  </SwipeCard>;
}
function ExampleMessage({ example, onNext, onPrevious, busy }: {
  example: typeof examples[number]; onNext: () => void; onPrevious: (() => void) | undefined; busy: boolean;
}) {
  const [body, setBody] = useState('');
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (edited) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setBody(example.reply); return; }
    let length = 0;
    const timer = window.setInterval(() => { length = Math.min(length + 10, example.reply.length); setBody(example.reply.slice(0, length)); if (length >= example.reply.length) window.clearInterval(timer); }, 25);
    return () => window.clearInterval(timer);
  }, [edited, example]);
  const ready = (edited || body.length === example.reply.length) && !!body.trim();
  return <div className={styles.demoEmail}><MailSurface label="Example email" demo>
    <MailSubject subject={example.subject} />
    <MailMessage from={example.from} to="me" body={example.incoming} />
    <MailComposer to={example.to} body={body} edited={edited} writing={!ready} disabled={busy} showEditHint={false}
      onChange={value => { setEdited(true); setBody(value); }}
      actions={<><button type="button" className={styles.dismiss} disabled={!onPrevious || busy} onClick={onPrevious}>Previous (Dismiss)</button>
        <button type="button" className={styles.primary} disabled={busy} onClick={onNext}>Next (Send)<span aria-hidden="true"> →</span></button></>} />
  </MailSurface></div>;
}
